import dayjs from 'dayjs'
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  MenuItem,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import SaleReferencesView from './saleContract/SaleReferencesView'
import PageHeader from '../components/PageHeader'

type FinalizedFilter = 'all' | 'yes' | 'no'

type SalesControlTabKey =
  | 'local-sales'
  | 'sales-allocation'
  | 'delivery-order'
  | 'receive-order'
  | 'invoice'
  | 'claim'
  | 'credit-note'
  | 'debit-note'
  | 'moving'
  | 'references'

interface SalesControlTabConfig {
  key: SalesControlTabKey
  label: string
  accessLabel: string
  accessForm: string
  evidence: string[]
  actions: string[]
  description: string
}

interface SaleControlFilters {
  startDate: string
  endDate: string
  isFinalized: FinalizedFilter
}

const FILTER_STORAGE_KEY = 'tlc:sales-control:filters'

const TAB_CONFIG: SalesControlTabConfig[] = [
  {
    key: 'local-sales',
    label: 'Local Sales',
    accessLabel: 'Sales Contract',
    accessForm: 'frmCtrlSalesMainSubFormLocalS',
    evidence: ['form_frmCtrlSalesMainSubFormLocalS.txt', 'form_frmCtrlSalesQryLocalS.txt', 'query_CtrlSalesQryLocalS.txt'],
    actions: ['Forced close', 'Re-open', 'Filling Docs'],
    description: 'Sales control list for local sales contracts. This tab is the default landing tab for the page.',
  },
  {
    key: 'sales-allocation',
    label: 'Sales Allocation',
    accessLabel: 'Sales Allocation',
    accessForm: 'frmCtrlSalesMainSubFormSaleAlc',
    evidence: [
      'form_frmCtrlSalesMainSubFormSaleAlc.txt',
      'form_frmCtrlSalesQrySaleAlc.txt',
      'query_CtrlSalesQrySalesAlc.txt',
    ],
    actions: ['New', 'Edit', 'Delete', 'Copy', 'Mass Upload', 'Print - BBGN', 'Print - VGM'],
    description: 'Allocation list and helper actions for sales allocation workflows.',
  },
  {
    key: 'delivery-order',
    label: 'Delivery Order',
    accessLabel: 'Delivery Order',
    accessForm: 'frmCtrlSalesMainSubFormDO',
    evidence: ['form_frmCtrlSalesMainSubFormDO.txt', 'form_frmCtrlSalesQryDO.txt', 'query_CtrlSalesQryDO.txt'],
    actions: ['New', 'Edit', 'Delete', 'Copy', 'Print - SA', 'Print - DO', 'Print - Dispatch'],
    description: 'Delivery-order tab with SA/DO/dispatch print workflows.',
  },
  {
    key: 'receive-order',
    label: 'Receive Order',
    accessLabel: 'Release Order',
    accessForm: 'frmCtrlSalesMainSubformRO',
    evidence: ['form_frmCtrlSalesMainSubformRO.txt', 'form_frmCtrlSalesQryRO.txt', 'query_CtrlSalesQryRO.txt'],
    actions: ['New', 'Edit', 'Delete', 'Print', 'Release Details'],
    description: 'Release-order workflow surfaced with the current web label Receive Order.',
  },
  {
    key: 'invoice',
    label: 'Invoice',
    accessLabel: 'Invoice',
    accessForm: 'frmCtrlSalesMainSubFormInvoice',
    evidence: [
      'form_frmCtrlSalesMainSubFormInvoice.txt',
      'form_frmCtrlSalesQryInvoice.txt',
      'query_CtrlSalesQryInvoice.txt',
    ],
    actions: ['New', 'Edit', 'Delete', 'Copy', 'Add DO', 'Print - Customer', 'Print - Customs', 'Print - PKL'],
    description: 'Invoice tab with customer/customs/PKL print variants.',
  },
  {
    key: 'claim',
    label: 'Claim',
    accessLabel: 'Claims',
    accessForm: 'frmCtrlSalesMainSubFormClaim',
    evidence: ['form_frmCtrlSalesMainSubFormClaim.txt', 'form_frmCtrlSalesQryClaim.txt', 'query_CtrlSalesQryClaim.txt'],
    actions: ['New', 'Edit', 'Delete', 'Print'],
    description: 'Claims tab that should follow Access field order and print evidence.',
  },
  {
    key: 'credit-note',
    label: 'Credit Note',
    accessLabel: 'Credit Note',
    accessForm: 'frmCtrlSalesMainSubFormCredit',
    evidence: [
      'form_frmCtrlSalesMainSubFormCredit.txt',
      'form_frmCtrlSalesQryCredit.txt',
      'query_CtrlSalesQryCredit.txt',
    ],
    actions: ['New', 'Edit', 'Delete', 'Print'],
    description: 'Credit note tab with its own query/report mapping.',
  },
  {
    key: 'debit-note',
    label: 'Debit Note',
    accessLabel: 'Debit Note',
    accessForm: 'frmCtrlSalesMainSubFormDebit',
    evidence: ['form_frmCtrlSalesMainSubFormDebit.txt', 'form_frmCtrlSalesQryDebit.txt', 'query_CtrlSalesQryDebit.txt'],
    actions: ['New', 'Edit', 'Delete', 'Print'],
    description: 'Debit note tab with separate print and edit flow.',
  },
  {
    key: 'moving',
    label: 'Moving',
    accessLabel: 'Internal Moving',
    accessForm: 'frmCtrlSalesMainSubFormMoving',
    evidence: ['form_frmCtrlSalesMainSubFormMoving.txt', 'form_frmCtrlSalesQryMoving.txt', 'query_CtrlSalesQryMoving.txt'],
    actions: ['New', 'Edit', 'Delete', 'Print'],
    description: 'Internal moving list supports multi-record print behavior.',
  },
  {
    key: 'references',
    label: 'References',
    accessLabel: 'References',
    accessForm: 'frmCtrlSalesMainSubFormReferences',
    evidence: [
      'form_frmCtrlSalesMainSubFormReferences.txt',
      'form_frmCtrlSalesQryRefCP.txt',
      'form_frmCtrlSalesQryRefCertification.txt',
    ],
    actions: ['Go to Page selector'],
    description: 'Reference hub for the Sales Control module.',
  },
]

function getDefaultFilters(): SaleControlFilters {
  const now = dayjs()
  const fiscalYearStart = now.month() >= 9 ? dayjs(`${now.year()}-10-01`) : dayjs(`${now.year() - 1}-10-01`)
  const fiscalYearEnd = now.month() >= 9 ? dayjs(`${now.year() + 1}-09-30`) : dayjs(`${now.year()}-09-30`)

  return {
    startDate: fiscalYearStart.format('YYYY-MM-DD'),
    endDate: fiscalYearEnd.format('YYYY-MM-DD'),
    isFinalized: 'all',
  }
}

function readStoredFilters(): SaleControlFilters {
  if (typeof window === 'undefined') return getDefaultFilters()

  const raw = window.localStorage.getItem(FILTER_STORAGE_KEY)
  if (!raw) return getDefaultFilters()

  try {
    const parsed = JSON.parse(raw) as Partial<SaleControlFilters>
    return {
      startDate: typeof parsed.startDate === 'string' ? parsed.startDate : getDefaultFilters().startDate,
      endDate: typeof parsed.endDate === 'string' ? parsed.endDate : getDefaultFilters().endDate,
      isFinalized:
        parsed.isFinalized === 'yes' || parsed.isFinalized === 'no' || parsed.isFinalized === 'all'
          ? parsed.isFinalized
          : 'all',
    }
  } catch {
    return getDefaultFilters()
  }
}

function TabSummary({ tab }: { tab: SalesControlTabConfig }) {
  return (
    <Card variant="outlined" sx={{ flex: 1, minHeight: 0 }}>
      <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Box>
          <Typography variant="h6" fontWeight={700}>
            {tab.label}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Access caption: {tab.accessLabel}
          </Typography>
        </Box>

        <Typography variant="body2">{tab.description}</Typography>

        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
          <Chip label={`Access form: ${tab.accessForm}`} size="small" variant="outlined" />
          {tab.actions.map((action) => (
            <Chip key={action} label={action} size="small" variant="outlined" />
          ))}
        </Stack>

        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
            Evidence
          </Typography>
          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
            {tab.evidence.map((file) => (
              <Chip key={file} label={file} size="small" />
            ))}
          </Stack>
        </Box>
      </CardContent>
    </Card>
  )
}

export default function SaleControlPage() {
  const location = useLocation()
  const navigate = useNavigate()
  const [filters, setFilters] = useState<SaleControlFilters>(readStoredFilters)

  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify(filters))
    }
  }, [filters])

  const activeKey = useMemo(() => {
    const segments = location.pathname.split('/').filter(Boolean)
    const candidate = segments[2] as SalesControlTabKey | undefined
    return TAB_CONFIG.some((tab) => tab.key === candidate) ? candidate : TAB_CONFIG[0].key
  }, [location.pathname])

  useEffect(() => {
    if (!location.pathname.startsWith('/sales/control')) return

    const segments = location.pathname.split('/').filter(Boolean)
    const candidate = segments[2] as SalesControlTabKey | undefined
    if (!TAB_CONFIG.some((tab) => tab.key === candidate)) {
      navigate('/sales/control/local-sales', { replace: true })
    }
  }, [location.pathname, navigate])

  const activeTab = TAB_CONFIG.find((tab) => tab.key === activeKey) ?? TAB_CONFIG[0]
  const hasInvalidRange = filters.startDate > filters.endDate

  const headerButtonSx = {
    minHeight: 24,
    px: 0.75,
    py: 0.15,
    fontSize: '0.67rem',
    lineHeight: 1.1,
    textTransform: 'none' as const,
  }

  const headerLabelSx = {
    whiteSpace: 'nowrap',
    fontSize: '0.72rem',
    lineHeight: 1.1,
  }

  const compactInputSx = {
    width: { xs: 124, sm: 132 },
    '& .MuiInputBase-root': {
      minHeight: 26,
      fontSize: '0.72rem',
    },
    '& .MuiInputBase-input': {
      py: 0.25,
      px: 0.75,
    },
  }

  const compactSelectSx = {
    width: 94,
    '& .MuiInputBase-root': {
      minHeight: 26,
      fontSize: '0.72rem',
    },
    '& .MuiSelect-select': {
      py: 0.25,
      px: 0.75,
    },
  }

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      <PageHeader
        title="Sale Control"
        module="Sales"
        actions={
          <Stack
            direction="row"
            spacing={0.45}
            alignItems="center"
            useFlexGap
            flexWrap="wrap"
            justifyContent={{ xs: 'flex-start', lg: 'flex-end' }}
            sx={{ width: '100%' }}
          >
            <Stack direction="row" spacing={0.45} alignItems="center" useFlexGap flexWrap="wrap">
              <Typography variant="body2" fontWeight={700} sx={headerLabelSx}>
                Shipment Period:
              </Typography>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  format="DD/MM/YYYY"
                  value={filters.startDate ? dayjs(filters.startDate) : null}
                  onChange={(d) => setFilters((prev) => ({ ...prev, startDate: d ? d.format('YYYY-MM-DD') : '' }))}
                  slotProps={{ textField: { size: 'small', sx: compactInputSx, inputProps: { 'aria-label': 'Shipment Period start date' } } }}
                />
                <DatePicker
                  format="DD/MM/YYYY"
                  value={filters.endDate ? dayjs(filters.endDate) : null}
                  onChange={(d) => setFilters((prev) => ({ ...prev, endDate: d ? d.format('YYYY-MM-DD') : '' }))}
                  slotProps={{
                    textField: {
                      size: 'small',
                      error: hasInvalidRange,
                      sx: compactInputSx,
                      inputProps: { 'aria-label': 'Shipment Period end date' },
                    },
                  }}
                />
              </LocalizationProvider>
            </Stack>

            <Stack direction="row" spacing={0.45} alignItems="center" useFlexGap flexWrap="wrap">
              <Typography variant="body2" fontWeight={700} sx={headerLabelSx}>
                Is Finalized:
              </Typography>
              <TextField
                select
                size="small"
                value={filters.isFinalized}
                onChange={(event) =>
                  setFilters((prev) => ({ ...prev, isFinalized: event.target.value as FinalizedFilter }))
                }
                sx={compactSelectSx}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="yes">Yes</MenuItem>
                <MenuItem value="no">No</MenuItem>
              </TextField>
            </Stack>

            <Button size="small" variant="outlined" sx={headerButtonSx} disabled={hasInvalidRange}>
              Export (Filter)
            </Button>
            <Button size="small" variant="outlined" sx={headerButtonSx}>
              Export Excel
            </Button>
          </Stack>
        }
      />
      {hasInvalidRange && (
        <Typography variant="caption" color="error" sx={{ alignSelf: 'flex-end', mb: 1 }}>
          Start date must be before or equal to end date
        </Typography>
      )}

      <Card sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        <Tabs
          value={activeKey}
          onChange={(_, value: SalesControlTabKey) => {
            navigate(`/sales/control/${value}`, { replace: false })
          }}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            minHeight: 38,
            '& .MuiTabs-indicator': { height: 2 },
            '& .MuiTab-root': {
              minHeight: 38,
              py: 0.25,
              px: 1.5,
              fontSize: '0.8rem',
              fontWeight: 600,
              textTransform: 'none',
            },
          }}
        >
          {TAB_CONFIG.map((tab) => (
            <Tab key={tab.key} label={tab.label} value={tab.key} />
          ))}
        </Tabs>

        <CardContent sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', pt: 2 }}>
          <Stack spacing={1.5} sx={{ flex: 1, minHeight: 0 }}>
            <Box>
              <Typography variant="subtitle1" fontWeight={700}>
                {activeTab.label}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {activeTab.accessLabel} - {activeTab.accessForm}
              </Typography>
            </Box>

            {activeTab.key === 'references' ? <SaleReferencesView /> : <TabSummary tab={activeTab} />}
          </Stack>
        </CardContent>
      </Card>
    </Box>
  )
}
